import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Tab, Checkbox, Item } from 'semantic-ui-react';
import { isMobileOnly } from 'mobile-device-detect';
import { useTextResources } from '../../language';
import { onError, onSuccess } from '../shared/messages';
import { useLoader } from '../../hooks/useLoader';
import TextInput from '../shared/TextInput';
import NationalitiesDropDown from '../shared/NationalitiesDropDown';
import { DatePicker } from '../shared/DatePicker';
import { GetCountries, CheckGuestTaxExcemptionEligibility } from '../../api/services/shared';
import { GetHotels } from '../../api/services/organization';
import { UpdateGuest } from '../../api/services/crm';
import { TaxAuthorityResultCodeTitles } from '../shared/constants'
import TaxExemptHistory from './taxExemptHistory'
import moment from 'moment';
import './style.css'
import _ from 'lodash';

const CheckTaxExemptionPopup = ({ controller, guest: guestDetails, reservation: reservationDetails, hotel: hotelDetails, onClose, startOpen, ...rest }) => {
    const [open, setOpen] = useState(startOpen);
    const [guest, setGuestDetails] = useState();
    const [reservation, setReservationDetails] = useState(reservationDetails);
    const [hotel, setHotel] = useState(hotelDetails);
    const [nationalityId, setNationality] = useState();
    const [countryCode, setCountryCode] = useState();
    const [passportNumber, setPassportNumber] = useState();
    const [showCheckResult, setShowCheckResult] = useState(false);
    const [checkResult, setCheckResult] = useState({
        isTaxExemptEligible: false,
        taxAuthorityCode: null,
        taxAuthorityMessage: null
    });
    const [countries, setCountries] = useState();
    const [isSuccessful, setIsSuccessful] = useState(false);

    const text = useTextResources()
    const loader = useLoader()

    useEffect(() => {
        if (controller) {
            window[controller] = (action, params) => {
                switch (action) {
                    case "open":
                        if (params) {
                            setGuest(params.guest)
                            setReservationDetails(params.reservation)
                            setHotel(params.hotel)
                        }
                        setOpen(true)
                        break;
                    default:
                        break;
                }
            }
        }

        (async () => {
            const countries = await GetCountries();
            setCountries(countries)
        })()

    }, [])

    useEffect(() => {
        setOpen(startOpen)
    }, [startOpen])

    useEffect(() => {
        setGuest(guestDetails);
    }, [guestDetails])

    useEffect(() => {
        if (reservationDetails) {
            setReservationDetails(reservationDetails)
        }
    }, [reservationDetails?.Id])

    const close = () => {
        setOpen(false)
        if (onClose) {
            const result = { isSuccess: isSuccessful, updatedPassportNumber: passportNumber, updatedNationalityId: nationalityId, isTaxExemptEligible: checkResult.isTaxExemptEligible };
            onClose(result)
            clearScreenData()
        }
    }

    useEffect(() => {
        if (guest) {
            try {
                setNationality(guest.NationalityId)
                setPassportNumber(guest.PassportNumber || guest.IdentityCardNumber)
            } catch (error) {
                onError(error)
            }
        }
    }, [guest, open])

    /*IMPORTANT! this is a temporary solution and should be removed once we finished with ID->Id*/
    const setGuest = (guest) => {
        if (guest) {
            const nationalityId = guest.NationalityId || guest.NationalityID;
            delete guest["NationalityID"];
            setGuestDetails({ ...guest, NationalityId: nationalityId })
        }
    }

    const clearScreenData = () => {
        setShowCheckResult(false);
        setIsSuccessful(false);
        setCheckResult({
            isTaxExemptEligible: false,
            taxAuthorityCode: null,
            taxAuthorityMessage: null
        });
    }

    const onNationalityChanged = (selectedItem) => {
        setNationality(selectedItem.Id)
        const country = countries.find(x => x.Id === selectedItem.CountryID)
        if (country) {
            setCountryCode(country.Alpha3Code)
        }
    }

    const saveGuestDetails = async () => {
        guest.PassportNumber = passportNumber;
        guest.NationalityId = nationalityId;

        try {
            await UpdateGuest(guest);
        } catch (e) {
            onError(e);
        }
    }

    const missingNationalityId = useCallback(() => {
        return !nationalityId
    }, [guest, nationalityId])

    const missingPassportNumber = useCallback(() => {
        return _.isEmpty(passportNumber)
    }, [guest, passportNumber])


    const isReadyForSave = () => {
        if (!missingNationalityId() && !missingPassportNumber()) {
            return true
        }
        else return false;
    }

    const sendForCheck = async () => {
        const isValid = isReadyForSave()
        if (!isValid) return;

        try {
            loader(true);

            let hotelData = hotel;
            if (!hotel.Location || !hotel.CompanyNumber) {
                const hotels = await GetHotels({ ID: hotel.Id, IncludeLocation: true });
                if (hotels && hotels[0]) {
                    hotelData = { ...hotel, ...hotels[0] };
                    setHotel(hotelData);
                }
            }

            const response = await CheckGuestTaxExcemptionEligibility({
                GuestsDetails: [{
                    CountryCode: countryCode,
                    PassportNumber: passportNumber,
                    CheckinDate: reservation.CheckInDate,
                    CheckoutDate: reservation.CheckOutDate,
                    ReservationID: reservation?.Id.toString(),
                    GuestID: guest.Id
                }],
                HotelDetails: {
                    HotelID: hotelData.Id.toString(),
                    Name: hotelData.Name,
                    Address: hotelData.Location?.PostFullAddress || '',
                    DealerIdentificationNumber: hotelData.CompanyNumber,
                }
            })

            if (response.IsSuccessful) {
                saveGuestDetails();
                if (response.Result) {
                    const result = response.Result[0];
                    setCheckResult({
                        isTaxExemptEligible: result.IsTaxExemptEligible,
                        taxAuthorityCode: TaxAuthorityResultCodeTitles[result.TaxAuthorityCode],
                        taxAuthorityMessage: result.TaxAuthorityMessage
                    });
                    setShowCheckResult(true);
                    setIsSuccessful(true);
                }
                onSuccess();
            }
            else {
                const errors = response.Errors;
                if (errors && errors.length > 0) {
                    const error = errors[0];
                    onError(error.Message);
                }
            }
            //close();
        } catch (e) {
            onError(e);
        }
        finally {
            loader(false);
        }
    }


    const historyPane = () => <Tab.Pane style={{ flex: 1 }}>
        <TaxExemptHistory reservationId={reservation.Id} />
    </Tab.Pane>


    const doTheCheckPane = () => <Tab.Pane style={{ flex: 1 }}>
        {reservation && guest &&
            <Form>
                <Form.Group unstackable widths={2}>
                    <Form.Field value={reservation.Number} label={text("ReservationNumber")} control={TextInput} disabled={true} className="form-field-disabled" />
                    <Form.Field value={guest.LastName + ' ' + guest.FirstName} label={text("Guest")} control={TextInput} disabled={true} className="disabled" />
                </Form.Group>
                <Form.Group unstackable widths={2}>
                    <DatePicker
                        label={text("CheckInDate")}
                        value={moment(reservation.CheckInDate).toDate()}
                        disabled={true} />
                    <DatePicker
                        label={text("CheckOutDate")}
                        value={moment(reservation.CheckOutDate).toDate()}
                        pointing="right"
                        disabled={true} />
                </Form.Group>
                <Form.Group unstackable widths={2}>
                    <Form.Field value={nationalityId}
                        label={text("Nationality")}
                        required
                        clearable
                        control={NationalitiesDropDown}
                        onChangeItem={(selectedItem) => onNationalityChanged(selectedItem)}
                        error={missingNationalityId() ? text("NationalityIsMandatory") : null} />
                    <Form.Field value={passportNumber}
                        label={text("Passport")}
                        required
                        clearable
                        control={TextInput}
                        onChange={setPassportNumber}
                        error={missingPassportNumber() ? text("PassportNumberIsMandatory") : null} />
                </Form.Group>
                {showCheckResult && checkResult && <Item>
                    <Item.Content>
                        <Item.Header as='h4' color={checkResult.isTaxExemptEligible ? 'green' : 'red'}>{text(checkResult.taxAuthorityCode)}</Item.Header>
                        <Item.Meta><Checkbox checked={checkResult.isTaxExemptEligible}
                            label={text("ExemptedFromTaxes")}
                            onChange={(val) => setCheckResult({ ...checkResult, isTaxExemptEligible: val })} disabled="true" /></Item.Meta>
                        <Item.Description>{checkResult.taxAuthorityMessage}</Item.Description>
                    </Item.Content>
                </Item>
                }
            </Form>
        }
    </Tab.Pane>

    return (
        <Modal size={isMobileOnly ? "fullscreen" : "tiny"} open={open} className={isMobileOnly ? "mobile" : ""} style={{ zIndex: 1000000 }} {...rest} >
            {!isMobileOnly && <Modal.Header>{text("CheckTaxExemption")}</Modal.Header>}
            <Modal.Content style={{ minHeight: 450 }}>
                <Tab menu={{ secondary: true, pointing: true }} style={{ height: "100%", display: "flex", flexDirection: "column" }}
                    panes={[
                        {
                            menuItem: text("Check"), render: doTheCheckPane
                        },
                        {
                            menuItem: text("History"), render: historyPane
                        }
                    ]}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button size={"small"} negative onClick={close}>{text("Close")}</Button>
                <Button size={"small"} positive onClick={sendForCheck}>{text("Check")}</Button>
            </Modal.Actions>
        </Modal>
    )
}

CheckTaxExemptionPopup.propTypes = {
    guest: PropTypes.object,
    reservation: PropTypes.object,
    hotel: PropTypes.object,
    onClose: PropTypes.func,
    startOpen: PropTypes.bool,
    controller: PropTypes.any,
}

export default CheckTaxExemptionPopup
